import { useState } from 'react';
import { ProjectResponse } from '@shared/interfaces/api/trackerRequest';
import './TrackerSettings.sass';

interface TrackerSettingsProps {
  project: ProjectResponse;
  onSettingsChange: (settings: { track_all_links?: boolean; track_all_buttons?: boolean }) => Promise<void>;
}

function TrackerSettings({ project, onSettingsChange }: TrackerSettingsProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [trackAllLinks, setTrackAllLinks] = useState(project.track_all_links);
  const [trackAllButtons, setTrackAllButtons] = useState(project.track_all_buttons);

  const handleToggleLinks = async () => {
    const newValue = !trackAllLinks;
    setIsUpdating(true);
    
    try {
      await onSettingsChange({ track_all_links: newValue });
      setTrackAllLinks(newValue);
    } catch (error) {
      console.error('Failed to update setting:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleButtons = async () => {
    const newValue = !trackAllButtons;
    setIsUpdating(true);
    
    try {
      await onSettingsChange({ track_all_buttons: newValue });
      setTrackAllButtons(newValue);
    } catch (error) {
      console.error('Failed to update setting:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="tracker-settings">
      <h2>Automatic Tracking</h2>
      <p className="settings-description">
        Enable automatic tracking for common user interactions. When enabled, the tracker will automatically capture these events without custom selectors.
      </p>

      <div className="settings-list">
        <div className="setting-item">
          <div className="setting-info">
            <div className="setting-header">
              <h3>Track All Link Clicks</h3>
              <span className={`badge ${trackAllLinks ? 'active' : 'inactive'}`}>
                {trackAllLinks ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p>
              Automatically track all link clicks on your website, including internal and external links. 
              Captures link URL, text, and distinguishes between internal/external navigation.
            </p>
          </div>
          <button
            className={`toggle-switch ${trackAllLinks ? 'active' : ''}`}
            onClick={handleToggleLinks}
            disabled={isUpdating}
            type="button"
            aria-label="Toggle link tracking"
          >
            <span className="toggle-slider" />
          </button>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <div className="setting-header">
              <h3>Track All Button Clicks</h3>
              <span className={`badge ${trackAllButtons ? 'active' : 'inactive'}`}>
                {trackAllButtons ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p>
              Automatically track all button clicks, including <code>&lt;button&gt;</code> elements and 
              <code>&lt;input type="submit"&gt;</code> / <code>&lt;input type="button"&gt;</code> elements.
              Captures button text, ID, and CSS classes.
            </p>
          </div>
          <button
            className={`toggle-switch ${trackAllButtons ? 'active' : ''}`}
            onClick={handleToggleButtons}
            disabled={isUpdating}
            type="button"
            aria-label="Toggle button tracking"
          >
            <span className="toggle-slider" />
          </button>
        </div>
      </div>

      {(trackAllLinks || trackAllButtons) && (
        <div className="settings-note">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 4V8M8 11H8.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span>
            Changes will take effect after regenerating the tracker. Click "Regenerate Tracker" to apply these settings.
          </span>
        </div>
      )}
    </div>
  );
}

export default TrackerSettings;
